import { defaultConfig, type AcousticConfig } from './config';
import { AcousticProtocol } from './protocol';
import { encodeWav } from './wav';

type ProgressFn = (current: number, total: number) => void;
type ErrorFn = (msg: string) => void;
type CompleteFn = () => void;
type SymbolFn = (index: number, symbol: number) => void;

const EXPORT_SAMPLE_RATE = 48000;
const HEAD_PAD = 0.15;
const TAIL_PAD = 0.05;

export class AcousticSender {
  audioCtx: AudioContext | null = null;
  private oscillator: OscillatorNode | null = null;
  private gainNode: GainNode | null = null;
  private analyser: AnalyserNode | null = null;
  sending = false;

  onProgress: ProgressFn | null = null;
  onComplete: CompleteFn | null = null;
  onError: ErrorFn | null = null;
  onSymbolStart: SymbolFn | null = null;

  private async ensureAudioContext(): Promise<AudioContext> {
    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioCtx = new Ctor();
    }
    if (this.audioCtx.state === 'suspended') {
      await this.audioCtx.resume();
    }
    return this.audioCtx;
  }

  async send(text: string, config?: Partial<AcousticConfig>): Promise<void> {
    if (this.sending) return;
    const cfg: AcousticConfig = { ...defaultConfig, ...config };
    let symbols: number[];
    try {
      symbols = await AcousticProtocol.encodeFrame(text, cfg);
    } catch (e) {
      this.onError?.((e as Error).message);
      return;
    }
    await this.playSymbols(symbols, cfg);
  }

  async sendSymbols(
    symbols: number[],
    config?: Partial<AcousticConfig>,
  ): Promise<void> {
    const cfg: AcousticConfig = { ...defaultConfig, ...config };
    return this.playSymbols(symbols, cfg);
  }

  /**
   * Render the same waveform that send() would produce, but into an
   * OfflineAudioContext, and return a 16-bit PCM mono WAV Blob.
   * Uses a fixed sample rate so the file is portable across devices.
   */
  async renderToWav(
    text: string,
    config?: Partial<AcousticConfig>,
  ): Promise<Blob> {
    const cfg: AcousticConfig = {
      ...defaultConfig,
      ...config,
      sampleRate: EXPORT_SAMPLE_RATE,
    };
    const symbols = await AcousticProtocol.encodeFrame(text, cfg);
    const symbolDuration = 1 / cfg.baudRate;
    const totalSeconds = HEAD_PAD + symbols.length * symbolDuration + TAIL_PAD;
    const totalSamples = Math.ceil(totalSeconds * cfg.sampleRate);

    const offlineCtx = new OfflineAudioContext(1, totalSamples, cfg.sampleRate);
    const gainNode = offlineCtx.createGain();
    gainNode.connect(offlineCtx.destination);
    const oscillator = offlineCtx.createOscillator();
    oscillator.type = 'sine';
    oscillator.connect(gainNode);

    this.scheduleSymbols(
      offlineCtx,
      gainNode,
      oscillator,
      symbols,
      cfg,
      offlineCtx.currentTime + HEAD_PAD,
    );

    const audioBuffer = await offlineCtx.startRendering();
    const samples = audioBuffer.getChannelData(0);
    return encodeWav(samples, cfg.sampleRate);
  }

  /**
   * Schedule the FSK symbol-by-symbol envelope on the given oscillator/gain
   * graph at startTime. Works for both AudioContext (live) and
   * OfflineAudioContext (export) — they share the BaseAudioContext API
   * surface that this method uses.
   */
  private scheduleSymbols(
    ctx: BaseAudioContext,
    gainNode: GainNode,
    oscillator: OscillatorNode,
    symbols: number[],
    cfg: AcousticConfig,
    startTime: number,
  ): { endTime: number } {
    const symbolDuration = 1 / cfg.baudRate;
    const toneRatio = 1 - (cfg.guardRatio || 0);
    const toneDuration = symbolDuration * toneRatio;
    const rampTime = 0.003;
    const freqs = cfg.freqs;

    gainNode.gain.setValueAtTime(0, ctx.currentTime);

    for (let i = 0; i < symbols.length; i++) {
      const freq = freqs[symbols[i]] ?? freqs[0];
      const symStart = startTime + i * symbolDuration;
      oscillator.frequency.setValueAtTime(freq, symStart);
      gainNode.gain.setValueAtTime(0, symStart);
      gainNode.gain.linearRampToValueAtTime(0.5, symStart + rampTime);
      gainNode.gain.setValueAtTime(0.5, symStart + toneDuration - rampTime);
      gainNode.gain.linearRampToValueAtTime(0, symStart + toneDuration);
    }

    const endTime = startTime + symbols.length * symbolDuration;
    oscillator.start(startTime);
    oscillator.stop(endTime + 0.01);
    return { endTime };
  }

  private async playSymbols(
    symbols: number[],
    cfg: AcousticConfig,
  ): Promise<void> {
    if (this.sending) throw new Error('Already sending');
    this.sending = true;
    let ctx: AudioContext;
    try {
      ctx = await this.ensureAudioContext();
    } catch (e) {
      this.sending = false;
      throw e;
    }
    return new Promise((resolve) => {
      cfg.sampleRate = ctx.sampleRate;

      this.analyser = ctx.createAnalyser();
      this.analyser.fftSize = 2048;

      this.gainNode = ctx.createGain();
      this.gainNode.connect(this.analyser);
      this.analyser.connect(ctx.destination);

      this.oscillator = ctx.createOscillator();
      this.oscillator.type = 'sine';
      this.oscillator.connect(this.gainNode);

      const startTime = ctx.currentTime + HEAD_PAD;
      this.scheduleSymbols(
        ctx,
        this.gainNode,
        this.oscillator,
        symbols,
        cfg,
        startTime,
      );

      const symbolDuration = 1 / cfg.baudRate;
      const totalSymbols = symbols.length;
      let lastFiredIdx = -1;
      const pollPeriod = Math.max(15, Math.min(50, (symbolDuration * 1000) / 2));
      const progressInterval = window.setInterval(() => {
        if (!this.sending) {
          window.clearInterval(progressInterval);
          return;
        }
        const elapsed = ctx.currentTime - startTime;
        const currentSym = Math.min(
          Math.floor(elapsed / symbolDuration),
          totalSymbols,
        );
        if (currentSym > lastFiredIdx && this.onSymbolStart) {
          for (let i = lastFiredIdx + 1; i <= currentSym && i < totalSymbols; i++) {
            this.onSymbolStart(i, symbols[i]);
          }
          lastFiredIdx = currentSym;
        }
        this.onProgress?.(currentSym, totalSymbols);
      }, pollPeriod);

      this.oscillator.onended = () => {
        window.clearInterval(progressInterval);
        this.sending = false;
        this.onProgress?.(totalSymbols, totalSymbols);
        this.onComplete?.();
        this.disconnectGraph();
        this.suspendAudioContext();
        resolve();
      };
    });
  }

  stop(): void {
    if (this.oscillator && this.sending) {
      try {
        this.oscillator.stop();
      } catch {
        /* noop */
      }
      this.sending = false;
    }
    this.disconnectGraph();
    this.suspendAudioContext();
  }

  private disconnectGraph(): void {
    try { this.oscillator?.disconnect(); } catch { /* noop */ }
    try { this.gainNode?.disconnect(); } catch { /* noop */ }
    try { this.analyser?.disconnect(); } catch { /* noop */ }
    this.oscillator = null;
    this.gainNode = null;
    this.analyser = null;
  }

  private suspendAudioContext(): void {
    if (this.audioCtx && this.audioCtx.state === 'running') {
      this.audioCtx.suspend().catch(() => {});
    }
  }

  release(): void {
    this.disconnectGraph();
    if (this.audioCtx && this.audioCtx.state !== 'closed') {
      this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
  }

  getAnalyser(): AnalyserNode | null {
    return this.analyser;
  }
}
